// Question du Conteur, en deux formes. Repliée (`compact`) : un bandeau tout entier cliquable, avec l'urgence, le
// compte à rebours, le titre, « Répondre ▾ » et « +N autres » s'il y a d'autres questions. Dépliée : la carte complète,
// avec ses options en gros boutons, la réponse libre et la conséquence si ignorée, dans 55 vh au plus et défilable.
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { MINUTE } from "../../shared/types";
import type { Sollicitation, SollicitationKind, SollicitationOption, Urgency } from "../../shared/types";
import { send } from "../store";
import "./SollicitationCard.css";

export interface SollicitationCardProps {
  s: Sollicitation;
  now: number;
  /** Forme repliée : le bandeau. */
  compact?: boolean;
  /** Bandeau : ouvre la carte complète au toucher. */
  onExpand?: () => void;
  /** Carte complète : la replie en bandeau (« Replier ▴ »). */
  onCollapse?: () => void;
  /** Bandeau : nombre d'autres questions en attente, affiché « +N autres ». */
  autres?: number;
}

const URGENCY_LABEL: Record<Urgency, string> = { immediat: "Immédiat", pressant: "Pressant", tranquille: "Tranquille" };
const KIND_LABEL: Record<SollicitationKind, string> = {
  combat: "Combat", evenement: "Événement", dialogue: "Dialogue", rencontre: "Rencontre",
  quete: "Quête", survie: "Survie", donjon: "Donjon",
};
const MAX_FREE_TEXT = 500;
const TICK_MS = 30_000;
/** Si le serveur ne répond pas, les boutons se rallument après ce délai. */
const SENDING_TIMEOUT_MS = 8_000;
const FREE_KEY = "__libre__";

/** Horloge vivante : part de l'heure serveur de la vue, avance avec le téléphone, se rafraîchit toutes les 30 s. */
export function useLiveNow(serverNow: number): number {
  const skew = useMemo(() => serverNow - Date.now(), [serverNow]);
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => clearInterval(id);
  }, []);
  return Date.now() + skew;
}

/** Durée lisible : « 3 h 12 », « 45 min », « moins de 10 min ». */
export function formatDuration(ms: number): string {
  const totalMin = Math.max(0, Math.floor(ms / MINUTE));
  if (totalMin < 10) return "moins de 10 min";
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")}`;
}

/** Compte à rebours : « 3 h 12 restantes », « moins de 10 min ! ». */
export function formatRemaining(ms: number): string {
  if (ms <= 0) return "temps écoulé";
  if (ms < 10 * MINUTE) return "moins de 10 min !";
  return `${formatDuration(ms)} restantes`;
}

const parisHourFmt = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", hour: "numeric", hour12: false });
const parisClockFmt = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit" });

/** Heure à Paris, ex. « 08:00 ». */
export function formatClock(ms: number): string {
  return parisClockFmt.format(new Date(ms));
}

/** Heures calmes (23 h – 8 h à Paris) : les échéances ne s'écoulent pas. */
function isQuietAt(ms: number): boolean {
  const hour = parisHourFmt.formatToParts(new Date(ms)).find((p) => p.type === "hour")?.value ?? "12";
  const h = (parseInt(hour, 10) || 0) % 24;
  return h >= 23 || h < 8;
}

function statusLine(s: Sollicitation): string {
  if (s.status === "expiree") return "Le temps a passé. Le Conteur raconte la suite.";
  const a = s.answer;
  if (!a) return "Question close.";
  const label = s.options.find((o) => o.id === a.optionId)?.label ?? a.text ?? "";
  return a.auto ? `Le Conteur a tranché à ta place : « ${label} »` : `Tu as répondu : « ${label} »`;
}

/** Compte à rebours d'une question ouverte, ou « suspendu » pendant les heures calmes (mention raccourcie au bandeau). */
function timerOf(s: Sollicitation, live: number, court: boolean): { text: string; quiet: boolean } {
  const quiet = isQuietAt(live);
  if (quiet) return { text: court ? "Suspendu jusqu'à 8 h" : "Temps suspendu jusqu'à 8 h", quiet };
  return { text: formatRemaining(s.deadline - live), quiet };
}

/** Question du Conteur : bandeau si `compact`, carte complète sinon. */
export function SollicitationCard(props: SollicitationCardProps) {
  return props.compact ? <SollicitationBanner {...props} /> : <SollicitationSheet {...props} />;
}

/** Forme repliée : un bandeau tout entier cliquable, avec « +N autres » quand d'autres questions attendent. */
function SollicitationBanner(props: SollicitationCardProps) {
  const { s, now, onExpand, autres = 0 } = props;
  const live = useLiveNow(now);
  const open = s.status === "ouverte";
  const { text, quiet } = timerOf(s, live, true);
  return (
    <button
      type="button"
      class={`sollicitation-banner sollicitation-card--${s.urgency}`}
      aria-expanded={false}
      onClick={onExpand}
    >
      <span class="sollicitation-banner-meta">
        <span class="sollicitation-card-urgency">{URGENCY_LABEL[s.urgency]}</span>
        {open ? (
          <span class={quiet ? "sollicitation-card-timer sollicitation-card-timer--quiet" : "sollicitation-card-timer"}>
            {text}
          </span>
        ) : null}
        {autres > 0 ? (
          <span class="sollicitation-banner-more">{`+${autres} autre${autres > 1 ? "s" : ""}`}</span>
        ) : null}
      </span>
      <span class="sollicitation-banner-title">{s.title}</span>
      <span class="sollicitation-banner-cta">Répondre ▾</span>
    </button>
  );
}

/**
 * Carte complète : en-tête (urgence, compte à rebours), titre et « Replier ▴ » fixes, corps défilant
 * (texte, options en gros boutons, réponse libre, conséquence) dans 55 vh au plus.
 */
function SollicitationSheet(props: SollicitationCardProps) {
  const { s, now, onCollapse } = props;
  const live = useLiveNow(now);
  const open = s.status === "ouverte";
  const { text, quiet } = timerOf(s, live, false);
  const [sending, setSending] = useState<string | null>(null);
  const [free, setFree] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const locked = !open || sending !== null;

  useEffect(() => () => {
    if (timer.current !== null) clearTimeout(timer.current);
  }, []);

  function lock(key: string) {
    setSending(key);
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => setSending(null), SENDING_TIMEOUT_MS);
  }

  function choose(opt: SollicitationOption) {
    if (locked) return;
    lock(opt.id);
    send({ t: "answer", sollicitationId: s.id, optionId: opt.id });
  }

  function sendFree() {
    const value = free.trim();
    if (locked || value === "") return;
    lock(FREE_KEY);
    send({ t: "answer", sollicitationId: s.id, text: value });
  }

  const cls = [
    "sollicitation-card",
    `sollicitation-card--${s.urgency}`,
    open ? "" : "sollicitation-card--closed",
  ].filter(Boolean).join(" ");

  return (
    <article class={cls} aria-label={s.title}>
      <header class="sollicitation-card-head">
        <span class="sollicitation-card-urgency">{URGENCY_LABEL[s.urgency]}</span>
        <span class="sollicitation-card-kind">{KIND_LABEL[s.kind]}</span>
        {open ? (
          <span class={quiet ? "sollicitation-card-timer sollicitation-card-timer--quiet" : "sollicitation-card-timer"}>
            {text}
          </span>
        ) : null}
      </header>

      <div class="sollicitation-card-titlebar">
        <h3 class="sollicitation-card-title">{s.title}</h3>
        {onCollapse ? (
          <button type="button" class="sollicitation-card-collapse" aria-expanded={true} onClick={onCollapse}>
            Replier ▴
          </button>
        ) : null}
      </div>

      <div class="sollicitation-card-body">
        <p class="sollicitation-card-prompt">{s.prompt}</p>

        {open ? (
          <div class="sollicitation-card-options" role="group" aria-label="Réponses possibles">
            {s.options.map((opt) => (
              <button
                key={opt.id}
                type="button"
                class={sending === opt.id ? "sollicitation-card-option sollicitation-card-option--sending" : "sollicitation-card-option"}
                disabled={locked}
                onClick={() => choose(opt)}
              >
                <span class="sollicitation-card-option-label">{opt.label}</span>
                {opt.hint ? <span class="sollicitation-card-option-hint">{opt.hint}</span> : null}
                {opt.id === s.defaultOptionId ? <span class="sollicitation-card-option-default">par défaut</span> : null}
              </button>
            ))}
          </div>
        ) : (
          <p class="sollicitation-card-status">{statusLine(s)}</p>
        )}

        {open && s.allowFreeText ? (
          <form class="sollicitation-card-free" onSubmit={(e) => { e.preventDefault(); sendFree(); }}>
            <textarea
              class="sollicitation-card-free-input"
              rows={2}
              maxLength={MAX_FREE_TEXT}
              placeholder="ou décris ta propre réaction…"
              aria-label="Ta propre réaction"
              value={free}
              disabled={locked}
              onInput={(e) => setFree(e.currentTarget.value)}
            />
            <button type="submit" class="sollicitation-card-send" disabled={locked || free.trim() === ""}>
              Envoyer ma réaction
            </button>
          </form>
        ) : null}

        {open && s.consequenceIfIgnored ? (
          <p class="sollicitation-card-consequence">Sans réponse : {s.consequenceIfIgnored}</p>
        ) : null}
      </div>
    </article>
  );
}
